/**
 * 身分：開通（建帳號／用池內帳號／重建失效身分）、切換、健康看板的重連與刪除。
 *
 * 職責邊界：**只**管「一顆身分怎麼誕生、換掉、收掉」。訊息送出在 `useSendActions`，
 * 批次與帳號池在 `useBatchActions`，提示在 `useNoticeQueue`。
 *
 * 三條開通入口共用同一個 `runOpening`：三段（登入→進房→掛線）與已耗秒數由 `OpeningPanel`
 * 依 `startedAt` 自己算；`runOpening` 永不拋錯，失敗留在 `opening`（同一塊有「重試」）。
 */
import { useCallback, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { randomNickname } from '@chat-rea/console-ui';
import type {
  Conversation,
  DroppedSession,
  HealthAccount,
  NoticeKind,
  Service,
  UseInboxResult,
} from '@chat-rea/console-ui';
import { client } from '../api';
import type { OpeningAttempt, OpeningState } from './consoleActions.shared';

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * 把焦點交回指令列。
 *
 * 2026-10-26 驗收：`/acct` 失敗後「開通失敗」面板留在畫面上，指令列**完全吃不到鍵盤**
 * （`/` 快捷鍵對 disabled 的 textarea 束手無策），只能先用滑鼠按「重試／關閉」。
 * 這裡在失敗、關閉面板時把焦點還回去，讓鍵盤流程不必中斷。
 *
 * 預設只在「焦點本來就掉在 body／面板裡」時才搶，避免把正在別處打字的人拉走。
 */
function focusCommandInputIfStuck(force = false): boolean {
  const input = document.querySelector<HTMLTextAreaElement>('textarea[data-testid="command-input"]');
  if (!input || input.disabled) return false;
  const active = document.activeElement as HTMLElement | null;
  const inOpeningPanel = Boolean(active?.closest?.('[data-testid="opening-panel"]'));
  const idle = !active || active === document.body || inOpeningPanel;
  if (!force && !idle) return false;
  input.focus();
  return document.activeElement === input;
}

export interface IdentityActionsDeps {
  inbox: UseInboxResult;
  conversations: Conversation[];
  currentService: Service;
  targetRoom: string;
  setBusy: Dispatch<SetStateAction<boolean>>;
  /** 手機看對話串（選了對話就切過去）。 */
  setMobileThread: Dispatch<SetStateAction<boolean>>;
  setActionError: (message: string | null) => void;
  setNotice: (message: string | null, kind?: NoticeKind) => void;
  markGone: (err: unknown, id: string) => boolean;
}

export interface IdentityActions {
  /** 三條開通入口共用的流程；帳號池那條（`useBatchActions`）也要用它。 */
  runOpening: (attempt: OpeningAttempt) => Promise<boolean>;
  /**
   * 上一次開通的原始錯誤（同步寫入，`runOpening` 一 resolve 就讀得到）。
   * 帳號頁那條訊息要用它分類，不能等 React 把 `opening` 畫出來才讀（那時已經晚一拍）。
   */
  lastOpeningErrorRef: MutableRefObject<string | null>;
  opening: OpeningState | null;
  /** 熱鍵（Esc）要讀「現在有沒有失敗面板」；用 ref 才不會讓 keydown 閉包拿到舊值。 */
  openingRef: MutableRefObject<OpeningState | null>;
  createError: string | null;
  createAccount: () => void;
  retryOpening: () => void;
  dismissOpening: () => void;
  reconnectAccount: (account: HealthAccount) => void;
  deleteAccount: (account: HealthAccount) => void;
  rebuildDropped: (session: DroppedSession) => void;
  openConversation: (id: string | null) => void;
  jumpToUnread: () => boolean;
}

export function useIdentityActions({
  inbox,
  conversations,
  currentService,
  targetRoom,
  setBusy,
  setMobileThread,
  setActionError,
  setNotice,
  markGone,
}: IdentityActionsDeps): IdentityActions {
  const { select, markRead, refresh, dismissDropped, activeId } = inbox;

  const [createError, setCreateError] = useState<string | null>(null);
  /**
   * 開通（建立新身分）的進度與失敗：三段＋已耗秒數＋「卡在哪」＋重試。
   * 放在這一層而不是按鈕裡，是因為三條入口（建帳號、用池內帳號、重建失效身分）
   * 都要在同一個位置說同一套話。
   */
  const [opening, setOpening] = useState<OpeningState | null>(null);

  /** 目前這輪開通（重試要讀最新的 attempt；不要用 state 閉包，否則會拿到舊的）。 */
  const openingRef = useRef<OpeningState | null>(null);
  openingRef.current = opening;

  /** 開通錯誤的同步副本：`runOpening` 的 Promise 一 resolve 就能被讀到（見介面註解）。 */
  const lastOpeningErrorRef = useRef<string | null>(null);

  /**
   * 開通一顆身分的共同流程：三條入口（建帳號／用池內帳號／重建失效身分）都走這裡。
   */
  const runOpening = useCallback(
    async (attempt: OpeningAttempt): Promise<boolean> => {
      setOpening({ attempt, startedAt: Date.now(), status: 'running', error: null });
      lastOpeningErrorRef.current = null;
      setCreateError(null);
      setActionError(null);
      setBusy(true);
      try {
        const created = await client.createAccount({
          service: attempt.service,
          nickname: attempt.nickname,
          // `room: null` 就不要送這個欄位（跨站且拿不到目標站房號時，交給後端決定）。
          ...(attempt.room ? { room: attempt.room } : {}),
          ...(attempt.loginMode ? { loginMode: attempt.loginMode } : {}),
          ...(attempt.accountUsername ? { accountUsername: attempt.accountUsername } : {}),
        });
        await refresh();
        select(created.id);
        void markRead(created.id);
        if (attempt.droppedId) dismissDropped(attempt.droppedId);
        // 成功就收掉進度條；成功的開通不需要留在畫面上。
        setOpening(null);
        if (attempt.notice) setNotice(attempt.notice);
        return true;
      } catch (err) {
        const message = errMessage(err);
        // 同步留一份原始原因：`usePoolAccount` 一 resolve 就要分類（不能等 React 重畫）。
        lastOpeningErrorRef.current = message;
        // 失敗留在同一塊（附「重試」），不再另外丟一條上方紅字：同一件事不要講兩次。
        setOpening((current) =>
          current
            ? { ...current, status: 'failed', error: message }
            : { attempt, startedAt: Date.now(), status: 'failed', error: message },
        );
        // 舊欄位保留給既有呼叫端（目前畫面不再顯示它）。
        setCreateError(message);
        // 面板出現後不要把鍵盤鎖在裡面：焦點若掉到 body／面板，就還給指令列。
        // （state 已排進 React，等它落地再聚焦，否則會抓不到剛重畫的 textarea。）
        window.setTimeout(() => focusCommandInputIfStuck(), 0);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [dismissDropped, markRead, refresh, select, setActionError, setBusy, setNotice],
  );

  /** 原封不動再開一次：同一個站、同一個暱稱、同一間房、同一種登入方式。 */
  const retryOpening = useCallback(() => {
    const current = openingRef.current;
    if (!current || current.status === 'running') return;
    void runOpening(current.attempt);
  }, [runOpening]);

  /**
   * 收掉失敗區塊（使用者按「關閉」或按 Esc）：連舊的紅色錯誤字一起收，不要關掉又冒出來。
   * 收完把焦點還給指令列——面板走了，鍵盤使用者應該回到剛剛在做的事。
   */
  const dismissOpening = useCallback(() => {
    setOpening(null);
    setCreateError(null);
    window.setTimeout(() => focusCommandInputIfStuck(true), 0);
  }, []);

  /** 一鍵建帳號：暱稱自動產生小寫英數（ek21 會拒底線），建完直接選中並重抓清單。 */
  const createAccount = useCallback(() => {
    // 失敗會留在開通區塊（含「重試」），不在這裡另外丟一條紅字。
    void runOpening({
      service: currentService,
      nickname: randomNickname(),
      room: targetRoom,
    });
  }, [currentService, runOpening, targetRoom]);

  /** 選一個對話＝選身分；順便把未讀歸零（後端權威，失敗會回報）。 */
  const openConversation = useCallback(
    (id: string | null) => {
      select(id);
      if (!id) return;
      setMobileThread(true);
      void markRead(id);
    },
    [select, markRead, setMobileThread],
  );

  /**
   * 把焦點真的移到某一列對話（`role="option"` 的 `tabIndex=-1`，只能用程式聚焦）。
   * 只改選取狀態而不動焦點，鍵盤使用者會卡在原地——那就是「Tab 被吃掉」的成因。
   * 回傳是否真的聚焦成功：沒成功就不要 preventDefault，讓原生 Tab 照常走。
   */
  const focusConversationRow = useCallback((id: string): boolean => {
    const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-conversation-id]'));
    const row = rows.find((node) => node.dataset.conversationId === id);
    // 桌機以外（缺少該列）就退回清單本體，至少不要把人留在原本的元素上。
    const target = row ?? document.querySelector<HTMLElement>('[data-testid="conversation-options"]');
    if (!target) return false;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'nearest' });
    return document.activeElement === target;
  }, []);

  /** 下一個有未讀的對話（環狀）並把焦點移過去；沒有就回 false，讓 Tab 保留原本行為。 */
  const jumpToUnread = useCallback((): boolean => {
    const total = conversations.length;
    if (total === 0) return false;
    const start = conversations.findIndex((item) => item.id === activeId);
    for (let step = 1; step <= total; step += 1) {
      const item = conversations[(start + step + total) % total];
      if (item && item.id !== activeId && item.unread > 0) {
        openConversation(item.id);
        return focusConversationRow(item.id);
      }
    }
    return false;
  }, [activeId, conversations, focusConversationRow, openConversation]);

  /**
   * 健康看板「重連」＝重打 join：Worker 會喚醒 DO、重新登入站方並進房。
   * 沒有房間的身分用目前挑的房間（跟建帳號同一套預設），避免「重連到不知道哪」。
   */
  const reconnectAccount = useCallback(
    (account: HealthAccount) => {
      const room = account.room ?? targetRoom;
      void (async () => {
        setBusy(true);
        setActionError(null);
        try {
          await client.joinRoom(account.id, room);
          await refresh();
          setNotice(`已重打 join：${account.nickname} → ${room}（若站方限流會再被擋一次）`);
        } catch (err) {
          if (!markGone(err, account.id)) setActionError(errMessage(err));
        } finally {
          setBusy(false);
        }
      })();
    },
    [markGone, refresh, setActionError, setBusy, setNotice, targetRoom],
  );

  /** 健康看板「刪除」：真的 DELETE session；刪到目前對話就順手選下一個。 */
  const deleteAccount = useCallback(
    (account: HealthAccount) => {
      void (async () => {
        setBusy(true);
        setActionError(null);
        try {
          await client.close(account.id);
          if (account.id === activeId) select(null);
          await refresh();
          setNotice(`已刪除身分 ${account.nickname}`);
        } catch (err) {
          // 已經不存在的 session 刪除會 404：那本來就是要的結果，當成功處理。
          if (markGone(err, account.id)) {
            await refresh();
            return;
          }
          setActionError(errMessage(err));
        } finally {
          setBusy(false);
        }
      })();
    },
    [activeId, markGone, refresh, select, setActionError, setBusy, setNotice],
  );

  /**
   * 「重建」一個已失效的身分：沿用原本的服務與房間，換一個新暱稱重開 session。
   * 舊 session 已經被回收，同一個 id 叫不回來（重打 join 也只會再 404）。
   */
  const rebuildDropped = useCallback(
    (session: DroppedSession) => {
      // 換一個新暱稱（舊 session 已經被回收，同一個 id 叫不回來）。
      const nickname = randomNickname();
      void runOpening({
        service: session.service,
        nickname,
        room: session.room ?? targetRoom,
        droppedId: session.id,
        notice: `已重建身分 ${nickname}（原本的 ${session.nickname} 已失效）`,
      });
    },
    [runOpening, targetRoom],
  );

  return {
    runOpening,
    lastOpeningErrorRef,
    opening,
    openingRef,
    createError,
    createAccount,
    retryOpening,
    dismissOpening,
    reconnectAccount,
    deleteAccount,
    rebuildDropped,
    openConversation,
    jumpToUnread,
  };
}
